/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button } from "@/components/ui/button";
import { FormControl, FormItem, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PlusIcon, Trash2 } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { cn } from "@/lib/utils";
import { ErpSku } from "./erp-sku";
import { RequiredInput } from "@/components/shared/required-input";

type DefaultUomSettingsItem = {
  default_custom_uom: string;
  sku?: string[];
};
type DefaultUomSettingsType = {
  value: DefaultUomSettingsItem[];
  onChange: (value: DefaultUomSettingsItem[]) => void;
  onBlur?: () => void;
  sncErpSku: any[];
};

export const DefaultUomSettings = ({
  value = [],
  onChange,
  onBlur,
  sncErpSku,
}: DefaultUomSettingsType) => {
  const { formState } = useFormContext();

  const getError = (index: number, field: "default_custom_uom" | "sku") => {
    const formErrors = formState.errors as unknown as Record<string, unknown>;
    const settingsError = formErrors?.settings as
      | Record<string, unknown>
      | undefined;
    const defaultUomErrors = settingsError?.default_uom_settings as
      | Array<Record<string, unknown>>
      | undefined;
    const itemError = defaultUomErrors?.[index]?.[field] as
      | { message?: string }
      | undefined;
    return itemError?.message ?? "";
  };
  const handleAdd = () => {
    onChange([...value, { default_custom_uom: "", sku: [] }]);
  };

  const handleRemove = (index: number) => {
    const nextValue = [...value];
    nextValue.splice(index, 1);
    onChange(nextValue);
  };

  const handleItemChange = (
    index: number,
    key: keyof DefaultUomSettingsItem,
    rawValue: string,
  ) => {
    const nextValue = value.map((item, itemIndex) => {
      if (itemIndex !== index) return item;

      return {
        ...item,
        [key]:
          key === "sku"
            ? rawValue
                .split(",")
                .map((sku) => sku.trim())
                .filter(Boolean)
            : rawValue,
      };
    });

    onChange(nextValue);
  };

  return (
    <FormItem>
      <FormLabel>Default uom settings</FormLabel>
      <FormControl>
        <div>
          {value.length === 0 ? (
            <div className="rounded-md border border-dashed border-primary/70 p-4 text-center">
              <Button
                variant="outline"
                className="mx-auto flex items-center gap-2"
                type="button"
                onClick={handleAdd}
              >
                <PlusIcon className="h-4 w-4" />
                Add default UOM setting
              </Button>
            </div>
          ) : (
            <div className="max-h-[588px]  overflow-y-auto border">
              <table className="w-full">
                <thead>
                  <tr className="sticky top-0">
                    <th className="text-left text-[13px] w-[400px] border px-3 py-2 bg-gray-50">
                      Default customer UOM <RequiredInput />
                    </th>
                    <th className="text-left text-[13px] border px-3 py-2 bg-gray-50">
                      SKUs
                    </th>
                    <th className="text-left w-[40px] border px-3 py-2 bg-gray-50"></th>
                  </tr>
                </thead>
                <tbody>
                  {value.map((item, index) => (
                    <tr key={index}>
                      <td className="border p-1 align-baseline">
                        <Input
                          value={item.default_custom_uom}
                          placeholder="Default customer UOM"
                          onChange={(event) =>
                            handleItemChange(
                              index,
                              "default_custom_uom",
                              event.target.value,
                            )
                          }
                          // onBlur={onBlur}
                          className={cn(
                            getError(index, "default_custom_uom")
                              ? "border-destructive"
                              : "",
                          )}
                        />
                        {getError(index, "default_custom_uom") ? (
                          <p className="text-xs text-destructive mt-1">
                            {getError(index, "default_custom_uom")}
                          </p>
                        ) : null}
                      </td>
                      <td className="border p-1 align-baseline">
                        <div /* className="translate-y-[-5px]" */>
                          <ErpSku
                            placeholder="Search ERP SKU"
                            multiple
                            options={sncErpSku}
                            onChange={(skuCodes) => {
                              const nextValue = value.map((item, itemIndex) => {
                                if (itemIndex !== index) return item;
                                return {
                                  ...item,
                                  sku: Array.isArray(skuCodes)
                                    ? skuCodes
                                    : skuCodes
                                      ? [skuCodes]
                                      : [],
                                };
                              });
                              onChange(nextValue);
                            }}
                            value={item.sku ?? []}
                            onBlur={onBlur ?? (() => {})}
                            bindLabel="erp_sku_name"
                            bindValue="erp_sku_code"
                            onValueChange={() => {}}
                            defaultValue={
                              item.sku?.length
                                ? item.sku.map((skuCode) => ({
                                    erp_sku_name: skuCode,
                                    erp_sku_code: skuCode,
                                  }))
                                : []
                            }
                          />
                        </div>
                      </td>
                      <td className="border p-1 align-baseline">
                        <Button
                          variant="ghost"
                          className={cn(
                            "h-9 w-9 rounded-full p-0 text-destructive hover:bg-destructive/10",
                          )}
                          type="button"
                          onClick={() => handleRemove(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {value.length > 0 ? (
            <Button
              variant="default"
              className="flex mt-4 px-4 py-1.5 h-auto items-center justify-center gap-2"
              type="button"
              onClick={handleAdd}
            >
              <PlusIcon className="h-4 w-4" />
              Add default UOM setting
            </Button>
          ) : null}
        </div>
      </FormControl>
    </FormItem>
  );
};
